import { z } from '@customerio/hydra-validation';
import { sanitizeRelaxedHtml } from '@/utils/sanitize';
import { isEmptyString } from '@/utils/string';

const ContentTemplateDetail = z.object({
  actions: z.array(z.object({ id: z.string().optional(), title: z.string(), type: z.string() })).default([]),
  footer: z.string().optional(),
  body: z.string().optional(),
  media: z.array(z.string()).optional(),
  header_text: z.string().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  type_name: z.string(),
});

/** A Twilio content template as the delivery body stores it: the template, plus the values for its variables. */
const TwilioContentTemplateData = z.object({
  contentTemplateId: z.string(),
  contentTemplate: z.object({ default_type: z.string(), details: z.array(ContentTemplateDetail) }),
  variablesMap: z.record(z.string(), z.string().nullish()),
});

export type TwilioContentTemplateData = z.infer<typeof TwilioContentTemplateData>;
type ContentTemplateDetail = z.infer<typeof ContentTemplateDetail>;

// Template types Ember draws as a card. The rest (list picker, authentication, catalog) fall back to a variable list.
const CARD_TYPE_NAMES: ReadonlySet<string> = new Set([
  'whatsapp/card',
  'twilio/media',
  'twilio/card',
  'twilio/call-to-action',
  'twilio/text',
  'twilio/quick-reply',
]);

const ParsedJson = z.object({ contentTemplateId: z.unknown().optional() }).passthrough();

function parseJson(body: string): z.infer<typeof ParsedJson> | undefined {
  try {
    return ParsedJson.parse(JSON.parse(body));
  } catch {
    return undefined;
  }
}

export function parseContentTemplateData(body: string): TwilioContentTemplateData | undefined {
  try {
    const result = TwilioContentTemplateData.safeParse(JSON.parse(body));
    return result.success ? result.data : undefined;
  } catch {
    return undefined;
  }
}

/** Ember's `isWhatsapp`: a body that is JSON carrying a `contentTemplateId` (callers apply it to Twilio deliveries only). */
export function isWhatsappContentTemplateBody(body: string): boolean {
  return body.includes('contentTemplateId') && parseJson(body)?.contentTemplateId != null;
}

/** A Design Studio SMS body wraps its content in `<x-sms>`; the server renders it. */
export function isDsSmsBody(body: string): boolean {
  return body.includes('<x-sms');
}

type TemplateVariable = { identifier: string; value: string };

export type ContentTemplateView =
  | { kind: 'card'; detail: ContentTemplateDetail; variables: TemplateVariable[] }
  | { kind: 'variables'; variables: TemplateVariable[] };

/** What a content-template body shows: a card for the template types Ember draws, otherwise its variable list. */
export function toContentTemplateView(data: TwilioContentTemplateData, body: string): ContentTemplateView {
  const variables = Object.entries(data.variablesMap).map(([identifier, value]) => ({
    identifier,
    value: value ?? '',
  }));
  const detail = data.contentTemplate.details.find((d) => d.type_name === data.contentTemplate.default_type);
  if (isEmptyString(body) || detail == null || !CARD_TYPE_NAMES.has(detail.type_name)) {
    return { kind: 'variables', variables };
  }
  return { kind: 'card', detail, variables };
}

/**
 * Fill `{{identifier}}` placeholders with the template's variables, then sanitize the result. The
 * replacement is a function so `$` sequences in a value (`$$`, `$&`) are inserted as written.
 */
export function fillTemplateVariables(text: string, variables: TemplateVariable[]): string {
  const filled = variables.reduce(
    (acc, { identifier, value }) => acc.replaceAll(`{{${identifier}}}`, () => value),
    text,
  );
  return sanitizeRelaxedHtml(filled);
}
